import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import { X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordConfirmError, setPasswordConfirmError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);

  useEffect(() => {
    // Check if we have a recovery token in the URL hash
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get("access_token");
    const type = hashParams.get("type");

    if (accessToken && type === "recovery") {
      setHasValidToken(true);
    } else {
      // If no valid token, redirect to forgot password page
      console.error("No valid recovery token found");
      navigate("/forgot-password");
    }
  }, [navigate]);

  const validatePassword = (pwd) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(pwd);
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);

    if (pwd && !validatePassword(pwd)) {
      setPasswordError("Password does not meet requirements");
    } else {
      setPasswordError("");
    }
  };

  const handlePasswordConfirmChange = (e) => {
    const pwd = e.target.value;
    setPasswordConfirm(pwd);

    if (pwd && pwd !== password) {
      setPasswordConfirmError("Passwords do not match");
    } else {
      setPasswordConfirmError("");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!password || !passwordConfirm) {
      alert("Please fill in all fields");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password does not meet requirements");
      return;
    }

    if (password !== passwordConfirm) {
      setPasswordConfirmError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await updatePassword(password);

      if (error) {
        console.error("Password update error:", error);
        alert(error.message || "Failed to reset password. Please try again.");
        setLoading(false);
        return;
      }

      console.log("Password reset successful");
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    password && passwordConfirm && !passwordError && !passwordConfirmError;

  if (!hasValidToken) {
    return null; // Will redirect in useEffect
  }

  return (
    <div>
      {/* Header */}
      <Header showBack={true} onBackClick={() => navigate("/login")} />

      <div className="p-4">
        <div className="w-full max-w-md mx-auto flex flex-col gap-6 md:gap-10 mt-6 md:mt-12">
          <h2 className="text-2xl font-bold text-foreground">Reset Password</h2>

          <p className="text-xs text-foreground/80 leading-relaxed">
            Please enter your new password.
          </p>

          <form onSubmit={handleResetPassword} className="space-y-10">
            {/* Password */}
            <div className="space-y-1">
              <Label
                htmlFor="password"
                className="text-xs text-muted-foreground font-normal"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder=""
                value={password}
                onChange={handlePasswordChange}
                className="h-12 px-4 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
                required
              />
              {passwordError && (
                <p className="text-[10px] text-red-500">{passwordError}</p>
              )}
              {!passwordError && (
                <p className="text-[10px] text-muted-foreground">
                  Password must be at least 8 characters and include uppercase,
                  lowercase, numbers, and special characters.
                </p>
              )}
            </div>

            {/* Password Confirm */}
            <div className="space-y-1">
              <Label
                htmlFor="passwordConfirm"
                className="text-xs text-muted-foreground font-normal"
              >
                Password Confirm
              </Label>
              <Input
                id="passwordConfirm"
                type="password"
                placeholder=""
                value={passwordConfirm}
                onChange={handlePasswordConfirmChange}
                className="h-12 px-4 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
                required
              />
              {passwordConfirmError && (
                <p className="text-[10px] text-red-500">
                  {passwordConfirmError}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={!isFormValid || loading}
              className="w-full h-12"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-6 w-full max-w-[320px] flex flex-col gap-8">
            {/* Close button */}
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/login");
                }}
                className="p-0"
              >
                <X className="w-6 h-6 text-[#7A7A7A]" />
              </button>
            </div>

            {/* Modal content */}
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h3 className="text-base font-semibold text-center text-foreground leading-relaxed">
                  Your password
                  <br />
                  has been changed
                </h3>
                <p className="text-sm text-center text-[#595959]">
                  You can now log in with your new password
                </p>
              </div>

              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate("/login");
                }}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
